/**
 * Strips write pills from the project header when the project is read-only
 * (a former client's project viewed by staff). The server refuses these
 * writes anyway; this keeps the header from offering them. Navigation, view,
 * search, export and download pills stay. The native shells' toolbar ids are
 * covered too, since they publish their own copies of these actions.
 */
import type { MobileHeaderAction } from './types';

const WRITE_ACTION_IDS = new Set([
  // BOQ
  'boq-add', 'boq-add-sheet', 'boq-import', 'boq-create', 'boq-delete-selected',
  // Documents
  'documents-new-folder', 'documents-upload', 'documents-bulk-delete', 'documents-preview-save',
  // Takeoff
  'takeoff-run-ai', 'takeoff-selection-edit-details', 'takeoff-selection-delete', 'takeoff-sheet-details',
  // Project email: every send writes to the project's correspondence
  'project-email-reply', 'project-email-reply-all', 'project-email-forward',
  'project-email-compose', 'project-email-prepare-tender',
  // Planner
  'planner-editor-save', 'planner-create', 'planner-create-group', 'planner-create-task',
  'planner-import', 'planner-undo', 'planner-redo', 'planner-sync-dates',
  // Site photos
  'site-photos-take-photo', 'site-photos-choose-photo',
  // RFIs
  'rfi-create', 'rfi-form-save', 'rfi-form-issue',
  // Applications
  'interim-add-variation', 'interim-add-material', 'interim-save', 'interim-delete',
  'interim-submit', 'interim-create', 'application-create', 'valuation-create',
  // Sharing mints a public link to the application
  'interim-share',
  // Events, instructions, dayworks
  'instruction-save', 'instruction-create', 'daywork-save', 'daywork-submit', 'daywork-create',
  'event-form-save', 'create-daywork', 'create-event', 'create-instruction', 'project-event-create',
  // Notes
  'project-notes-cancel', 'project-notes-save', 'project-notes-pin', 'project-notes-edit',
  'project-notes-delete', 'project-notes-add',
  // Health and safety
  'hs-create', 'hs-create-template', 'hs-create-blank', 'hs-edit', 'hs-new-version', 'hs-progress',
  'hs-recall-draft', 'hs-save-template', 'hs-archive', 'hs-restore-draft', 'hs-delete',
  'hs-editor-add-item', 'hs-editor-cancel', 'hs-editor-save',
  'hs-toolbox-create', 'hs-toolbox-edit', 'hs-toolbox-deliver', 'hs-toolbox-back-to-draft', 'hs-toolbox-delete',
  'hs-near-miss-create', 'hs-near-miss-edit', 'hs-near-miss-close', 'hs-near-miss-reopen', 'hs-near-miss-delete',
  'hs-plant-create', 'hs-plant-edit', 'hs-plant-complete', 'hs-plant-reopen', 'hs-plant-save-template', 'hs-plant-delete',
  'plant-create', 'plant-detail-save', 'plant-detail-cancel',
  // Project details and team
  'project-admin-cancel', 'project-admin-save', 'project-members-add',
  // Native shell toolbar ids for the same actions (navigation.ts)
  'admin-cancel', 'admin-save', 'hs-editor:cancel', 'hs-editor:save',
]);

// Families whose every member writes (upload flow steps, per-field inputs).
const WRITE_ACTION_PREFIXES = ['takeoff-upload'];

// The native H&S record menu is all lifecycle writes apart from its downloads.
const HS_MENU_READ_IDS = new Set(['hs-menu:download-pdf', 'hs-menu:toolbox-download', 'hs-menu:plant-download']);

export function isProjectWriteAction(id: string): boolean {
  if (id.startsWith('hs-menu:')) return !HS_MENU_READ_IDS.has(id);
  return WRITE_ACTION_IDS.has(id) || WRITE_ACTION_PREFIXES.some((prefix) => id.startsWith(prefix));
}

/** Removes write pills, and write rows inside menus; a menu left empty goes too. */
export function withoutProjectWriteActions(actions: readonly MobileHeaderAction[]): MobileHeaderAction[] {
  return actions.flatMap((action) => {
    if (isProjectWriteAction(action.id)) return [];
    if (!action.subActions) return [action];
    const subActions = action.subActions.filter((sub) => !isProjectWriteAction(sub.id));
    if (subActions.length === 0) return [];
    return [{ ...action, subActions }];
  });
}
